import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { financialsApi } from '@/lib/apiClient';
import { fmtDate } from '@/lib/dates';
import type { WipRow } from '@/lib/wip';
import {
  normalizeHistory, formatPeriod, compareCodes, SOURCE_LABEL,
  type HistoryPoint,
} from '@/lib/wipHistory';

// Frozen forecast history for one job. Finance asked for the monthly forecast to
// be kept so this month's cost-to-complete can be built against last month's,
// instead of the numbers only ever existing as "right now" (product owner, 2026-07-28).
//
// Collapsed by default and fetched only when opened — most visits to the tab are
// about the current figures, and the payload carries every snapshot's cost-code
// detail.

const money = (v: number | null | undefined) => v == null ? '—' : '$' + Math.round(v).toLocaleString('en-US');
const moneyC = (v: number | null | undefined) => {
  if (v == null) return '—';
  const a = Math.abs(v), sign = v < 0 ? '-' : '';
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e3)}K`;
  return `${sign}$${Math.round(a)}`;
};
const pct1 = (v: number | null) => v == null ? '—' : `${(v * 100).toFixed(1)}%`;

const css = `
  .fh-head { display:flex; align-items:center; gap:10px; flex-wrap:wrap; cursor:pointer; user-select:none; }
  .fh-head h3 { margin:0 !important; }
  .fh-caret { font-size:11px; color:var(--ew-n-500); transition:transform .12s ease; }
  .fh-caret.open { transform:rotate(90deg); }
  .fh-count { font:var(--ew-fw-semibold) 10.5px/1.6 var(--ew-font); letter-spacing:.04em; text-transform:uppercase; background:var(--ew-n-100); color:var(--ew-n-600); border-radius:var(--ew-r-pill); padding:2px 9px; }
  .fh-src { display:inline-block; font:var(--ew-fw-semibold) 9.5px/1.6 var(--ew-font); letter-spacing:.04em; text-transform:uppercase; border-radius:var(--ew-r-sm); padding:1px 6px; border:1px solid transparent; white-space:nowrap; }
  .fh-src.month-end { background:#E7F0F8; color:#15659E; border-color:#CFE0EF; }
  .fh-src.manual { background:#E7F0F8; color:#15659E; border-color:#CFE0EF; }
  .fh-src.forecast { background:#FFFDF5; color:#6B5D1F; border-color:#E7D9A6; }
  .fh-src.portfolio { background:var(--ew-n-50); color:var(--ew-n-600); border-color:var(--ew-n-200); }
  .fh-d { display:block; font:var(--ew-fw-semibold) 10.5px/1.5 var(--ew-font); font-variant-numeric:tabular-nums; margin-top:2px; white-space:nowrap; }
  .fh-d.up { color:var(--ew-green-700); }
  .fh-d.down { color:#B5611A; }
  .fh-d.flat { color:var(--ew-n-400); }
  .fh-tbl tbody tr.sel { background:#FBFBFA; }
  .fh-open { background:none; border:none; padding:0; font:var(--ew-fw-semibold) 12px/1 var(--ew-font); color:var(--ew-quality); cursor:pointer; text-decoration:underline; }
  .fh-open:disabled { color:var(--ew-n-400); cursor:default; text-decoration:none; }
  .fh-cmp { background:#FBFBFA; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:12px 14px; margin-top:12px; }
  .fh-cmp h4 { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-n-500); margin:0 0 4px; }
  .fh-cmp .sub { font-size:12px; color:var(--ew-n-500); margin-bottom:10px; }
  .fh-scroll { overflow-x:auto; }
  .fh-note { font-size:12.5px; line-height:1.45; color:var(--ew-n-500); }
`;

/** A signed delta cell. Direction alone isn't meaning: on profit, up is good;
 *  on cost, up is bad — so the caller says which way is favourable. */
function Delta({ value, fmt, good = 'up' }: { value: number | null; fmt: (n: number) => string; good?: 'up' | 'down' }) {
  if (value == null) return null;
  // Sub-dollar movement is rounding, not a change worth colouring.
  if (Math.abs(value) < 1) return <span className="fh-d flat">no change</span>;
  const favourable = good === 'up' ? value > 0 : value < 0;
  return (
    <span className={`fh-d ${favourable ? 'up' : 'down'}`}>
      {value > 0 ? '▲' : '▼'} {fmt(Math.abs(value))}
    </span>
  );
}

export default function ForecastHistoryCard({
  projectId, liveRows, liveLabel,
}: {
  projectId: number;
  /** The live computed cost-code rows, so the newest freeze can be compared
   *  against what the job looks like now. */
  liveRows: WipRow[];
  liveLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);

  const q = useQuery({
    queryKey: ['financials', projectId, 'history'],
    queryFn: () => financialsApi.history(projectId),
    enabled: open,
    staleTime: 60_000,
  });

  const points = useMemo(() => normalizeHistory(q.data?.snapshots ?? []), [q.data]);

  // Compare the selected freeze against the next OLDER one that carries
  // cost-code detail; for the newest freeze, against the live figures. Portfolio
  // snapshots hold no code detail, so they're skipped as a comparison basis
  // rather than compared as zeros.
  const comparison = useMemo(() => {
    if (selected == null) return null;
    const i = points.findIndex((p) => p.id === selected);
    if (i < 0 || !points[i].rows) return null;
    const older = points.slice(i + 1).find((p) => p.rows);
    if (i === 0) {
      return { basis: `now (${liveLabel})`, deltas: compareCodes(liveRows, points[i].rows!), reversed: true, point: points[i] };
    }
    if (!older) return { basis: null, deltas: [], reversed: false, point: points[i] };
    return { basis: formatPeriod(older.period), deltas: compareCodes(points[i].rows!, older.rows!), reversed: false, point: points[i] };
  }, [selected, points, liveRows, liveLabel]);

  const prior = (i: number): HistoryPoint | null => points[i + 1] ?? null;

  return (
    <div className="fin-card" style={{ marginTop: 'var(--ew-s-4)' }}>
      <style>{css}</style>
      <div className="fh-head" onClick={() => setOpen((o) => !o)} role="button" tabIndex={0}
           onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}>
        <span className={`fh-caret${open ? ' open' : ''}`}>▶</span>
        <h3>Forecast History</h3>
        {points.length > 0 && <span className="fh-count">{points.length} period{points.length === 1 ? '' : 's'}</span>}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ew-n-500)' }}>
          {open ? 'Hide' : 'Month-by-month frozen forecasts'}
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 'var(--ew-s-4)' }}>
          {q.isLoading && <div className="fh-note">Loading history…</div>}
          {q.isError && <div className="fh-note">Couldn’t load forecast history.</div>}
          {!q.isLoading && !q.isError && points.length === 0 && (
            <div className="fh-note">
              No frozen forecasts yet for this job. A snapshot is taken every time the forecast is
              saved, and the month-end freeze records every job automatically once it’s enabled.
            </div>
          )}

          {points.length > 0 && (
            <>
              {/* Each row is the job as it stood when that snapshot was taken, so its Actual
                  is the cost already synced then. A month's own view (View menu, above)
                  counts every entry dated in the month, including ones posted after the
                  freeze, so the two can differ for the same month. */}
              {points.some((p) => p.source === 'month-end' || p.source === 'month-close') && (
                <div className="fh-note" style={{ marginBottom: 8 }}>
                  Figures are frozen when each snapshot was taken, except that a month accounting has not
                  closed keeps the forecast as its PM last updated it. To see a month with every entry
                  dated in it, including ones posted afterward, use the View menu at the top of this tab.
                </div>
              )}
              <div className="fh-scroll">
                <table className="fin-tbl fh-tbl">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Revised</th>
                      <th>Actual</th>
                      <th>EAC</th>
                      <th>Proj. Profit</th>
                      <th>Margin</th>
                      <th>Super Margin</th>
                      <th>Billed</th>
                      <th style={{ textAlign: 'left' }}>Compare</th>
                    </tr>
                  </thead>
                  <tbody>
                    {points.map((p, i) => {
                      const pr = prior(i);
                      const m = p.metrics, pm = pr?.metrics ?? null;
                      const d = (k: 'revised' | 'actual' | 'eac' | 'projectedProfit' | 'billed') =>
                        pm ? m[k] - pm[k] : null;
                      return (
                        <tr key={p.id} className={selected === p.id ? 'sel' : undefined}>
                          <td>
                            <b>{formatPeriod(p.period)}</b>
                            <div style={{ fontSize: 11.5, color: 'var(--ew-n-500)', marginTop: 2 }}>
                              {fmtDate(p.asOf)}{p.by ? ` · ${p.by}` : ''}
                            </div>
                            {p.forecastEdited && (
                              <div style={{ fontSize: 11.5, color: 'var(--ew-n-500)', marginTop: 2 }}>
                                Forecast updated {fmtDate(p.forecastEdited.at)}{p.forecastEdited.by ? ` · ${p.forecastEdited.by}` : ''}
                              </div>
                            )}
                            <div style={{ marginTop: 3, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                              {p.source && <span className={`fh-src ${p.source}`}>{SOURCE_LABEL[p.source] ?? p.source}</span>}
                              {p.inPeriod > 1 && (
                                <span style={{ fontSize: 11, color: 'var(--ew-n-500)' }} title={`${p.inPeriod} snapshots this period; showing the one that stands`}>
                                  +{p.inPeriod - 1} more
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{money(m.revised)}<Delta value={d('revised')} fmt={moneyC} /></td>
                          <td>{money(m.actual)}<Delta value={d('actual')} fmt={moneyC} good="down" /></td>
                          <td>{money(m.eac)}<Delta value={d('eac')} fmt={moneyC} good="down" /></td>
                          <td style={{ fontWeight: 600 }}>
                            {money(m.projectedProfit)}<Delta value={d('projectedProfit')} fmt={moneyC} />
                          </td>
                          <td>
                            {pct1(m.projectedMarginPct)}
                            {pm && Math.abs(m.projectedMarginPct - pm.projectedMarginPct) >= 0.0005 && (
                              <span className={`fh-d ${m.projectedMarginPct > pm.projectedMarginPct ? 'up' : 'down'}`}>
                                {m.projectedMarginPct > pm.projectedMarginPct ? '▲' : '▼'} {Math.abs((m.projectedMarginPct - pm.projectedMarginPct) * 100).toFixed(1)} pts
                              </span>
                            )}
                          </td>
                          {/* Snapshots taken before indirects were captured can't
                              show super margin. Blank with a reason beats
                              printing projected margin twice. */}
                          <td style={{ color: m.superMarginPct == null ? 'var(--ew-n-400)' : undefined }}
                              title={m.superMarginPct == null ? 'This snapshot predates indirect-cost capture' : undefined}>
                            {m.superMarginPct == null ? 'n/a' : pct1(m.superMarginPct)}
                          </td>
                          <td>{money(m.billed)}<Delta value={d('billed')} fmt={moneyC} /></td>
                          <td style={{ textAlign: 'left' }}>
                            <button className="fh-open"
                                    disabled={!p.rows}
                                    title={p.rows ? 'Cost-code movement' : 'Portfolio snapshots don’t carry cost-code detail'}
                                    onClick={() => setSelected(selected === p.id ? null : p.id)}>
                              {p.rows ? (selected === p.id ? 'Hide codes' : 'By cost code') : '—'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="fh-note" style={{ marginTop: 10 }}>
                One row per accounting month — where a month holds several snapshots, the month-end
                freeze is shown, otherwise the latest. Deltas compare each period against the one
                below it. Figures are recomputed from the frozen facts with the same calculation this
                tab uses, so they match what the page showed at the time.
              </div>

              {comparison && (
                <div className="fh-cmp">
                  <h4>Cost-code movement · {formatPeriod(comparison.point.period)}</h4>
                  {comparison.basis == null ? (
                    <div className="fh-note">
                      No earlier snapshot with cost-code detail to compare against — this is the
                      first frozen forecast for the job.
                    </div>
                  ) : (
                    <>
                      <div className="sub">
                        {comparison.reversed
                          ? <>How the job has moved <b>since</b> this freeze — {formatPeriod(comparison.point.period)} → {comparison.basis}.</>
                          : <>Change from <b>{comparison.basis}</b> to <b>{formatPeriod(comparison.point.period)}</b>.</>}
                        {' '}Biggest movers first.
                      </div>
                      <div className="fh-scroll">
                        <table className="fin-tbl">
                          <thead>
                            <tr>
                              <th>Cost code</th>
                              <th>Was CTC</th>
                              <th>CTC</th>
                              <th>Δ CTC</th>
                              <th>Was EAC</th>
                              <th>EAC</th>
                              <th>Δ EAC</th>
                            </tr>
                          </thead>
                          <tbody>
                            {comparison.deltas.filter((c) => Math.abs(c.eacDelta) >= 1 || Math.abs(c.ctcDelta) >= 1).map((c) => (
                              <tr key={c.costCode}>
                                <td>
                                  <b>{c.costCode}</b>
                                  {c.costCodeName && <div style={{ fontSize: 11.5, color: 'var(--ew-n-500)' }}>{c.costCodeName}</div>}
                                </td>
                                <td style={{ color: 'var(--ew-n-500)' }}>{money(c.prevCtc)}</td>
                                <td>{money(c.ctc)}</td>
                                <td style={{ color: c.ctcDelta > 0 ? '#B5611A' : 'var(--ew-green-700)' }}>
                                  {c.ctcDelta > 0 ? '+' : ''}{money(c.ctcDelta)}
                                </td>
                                <td style={{ color: 'var(--ew-n-500)' }}>{money(c.prevEac)}</td>
                                <td>{money(c.eac)}</td>
                                <td style={{ fontWeight: 600, color: c.eacDelta > 0 ? '#B5611A' : 'var(--ew-green-700)' }}>
                                  {c.eacDelta > 0 ? '+' : ''}{money(c.eacDelta)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {comparison.deltas.every((c) => Math.abs(c.eacDelta) < 1 && Math.abs(c.ctcDelta) < 1) && (
                        <div className="fh-note">No cost code moved by more than a dollar between these two points.</div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
